/**
 * Landing page text/visibility resolution.
 *
 * The admin CMS (LandingPageWizard) authors a `landing_config` JSONB blob on
 * `organizations` (org mode) or `device_pwa_config` (device mode). Device
 * values win via the `get_device_landing_context` RPC, which COALESCEs the
 * device override over the organization default before it reaches the PWA.
 *
 * This helper turns that raw config into the small, presentational contract the
 * shared LoginPage consumes, so OrgLandingPage and DeviceLandingPage resolve it
 * identically (no duplicated "!== false" defaults or empty-string handling).
 */
export interface LandingConfig {
  headline?: string | null;
  subheadline?: string | null;
  show_logo?: boolean;
  show_headline?: boolean;
  show_subheadline?: boolean;
  [key: string]: unknown;
}

export interface LandingText {
  /** Custom headline, or undefined to fall back to the org name. */
  headline?: string;
  /** Custom subheadline, or undefined to fall back to the default copy. */
  subheadline?: string;
  showLogo: boolean;
  showHeadline: boolean;
  showSubheadline: boolean;
}

/**
 * Resolve the presentational landing text/visibility from a raw config.
 *
 * - Empty/whitespace-only strings are treated as "not set" so the caller falls
 *   back to its default copy rather than rendering a blank heading.
 * - Visibility flags default to `true` (shown) unless explicitly `false`,
 *   matching how the CMS persists `show_*` toggles.
 */
export function resolveLandingText(config?: LandingConfig | null): LandingText {
  const headline = typeof config?.headline === 'string' ? config.headline.trim() : '';
  const subheadline =
    typeof config?.subheadline === 'string' ? config.subheadline.trim() : '';

  return {
    headline: headline || undefined,
    subheadline: subheadline || undefined,
    showLogo: config?.show_logo !== false,
    showHeadline: config?.show_headline !== false,
    showSubheadline: config?.show_subheadline !== false,
  };
}
